import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Navigation, Loader2, Check, Sparkles } from "lucide-react";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useTranslation } from "react-i18next";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { useGlobal } from "@/context/GlobalContext";
import { zonedToUtcIso } from "@/lib/global";

const ALL_TZ = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];

const CATEGORY_OPTIONS = [
  { key: "concert", label: "Concert" },
  { key: "sports", label: "Sports" },
  { key: "dinner", label: "Dinner / Date" },
  { key: "business", label: "Business" },
  { key: "social", label: "Social" },
  { key: "other", label: "Other" },
];

const SAMPLE_IMAGES = {
  concert: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1200&q=70",
  sports: "https://images.unsplash.com/photo-1569863959165-56dae551d4fc?w=1200&q=70",
  dinner: "https://images.unsplash.com/photo-1575390130709-7b5fee2919e4?w=1200&q=70",
  business: "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=1200&q=70",
  social: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200&q=70",
  other: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1200&q=70",
};

export default function PostEvent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { deviceTz } = useGlobal();
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "concert",
    city: "",
    venue: "",
    starts_at: "",
    image_url: "",
    ticket_type: "fixed",
    price: "",
    starting_bid: "",
    bid_increment: "5",
    auction_ends_at: "",
    quantity: "1",
    video_url: "",
    lat: null,
    lng: null,
    country: null,
    country_code: null,
    timezone: deviceTz,
  });
  const [busy, setBusy] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const geo = useGeolocation();

  const aiGenerate = async () => {
    if (!form.title) {
      toast.error("Add a title first");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/event-description", {
        title: form.title,
        category: form.category,
        city: form.city,
        bullets: form.description || "",
      });
      set("description", data.description);
      toast.success("AI description generated");
    } catch (err) {
      console.error("[AI] description failed", err);
      toast.error(err.response?.data?.detail || "AI failed");
    } finally {
      setAiLoading(false);
    }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const useCurrentLocation = async () => {
    try {
      const loc = await geo.requestLocation();
      setForm((p) => ({ ...p, lat: loc.lat, lng: loc.lng }));
      toast.success(`Pinned to your location (±${Math.round(loc.accuracy || 0)}m)`);
    } catch (err) {
      toast.error(err.message || "Couldn't get your location");
    }
  };

  const lookupCity = async () => {
    const q = [form.venue, form.city].filter(Boolean).join(", ");
    if (!q) {
      toast.error("Add a city first");
      return;
    }
    setGeocoding(true);
    try {
      const { data } = await api.get("/geo/geocode", { params: { q } });
      if (!data.found) {
        toast.error("Couldn't find that location");
        return;
      }
      setForm((p) => ({ ...p, lat: data.lat, lng: data.lng }));
      toast.success(`Pinned: ${data.display_name?.split(",").slice(0, 2).join(", ") || q}`);
    } catch (err) {
      console.error("[Geocode] failed", err);
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally {
      setGeocoding(false);
    }
  };

  const clearPin = () => setForm((p) => ({ ...p, lat: null, lng: null }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        city: form.city,
        venue: form.venue,
        starts_at: zonedToUtcIso(form.starts_at, form.timezone),
        image_url: form.image_url || SAMPLE_IMAGES[form.category],
        video_url: form.video_url || null,
        ticket_type: form.ticket_type,
        quantity: parseInt(form.quantity) || 1,
        lat: form.lat,
        lng: form.lng,
        country: form.country,
        country_code: form.country_code,
        timezone: form.timezone,
      };
      if (form.ticket_type === "fixed") {
        payload.price = parseFloat(form.price);
      } else if (form.ticket_type === "auction") {
        payload.starting_bid = parseFloat(form.starting_bid);
        payload.bid_increment = parseFloat(form.bid_increment) || 5;
        payload.auction_ends_at = form.auction_ends_at ? zonedToUtcIso(form.auction_ends_at, form.timezone) : null;
      }
      const { data } = await api.post("/events", payload);
      toast.success(t("common.toast_posted"));
      navigate(`/event/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] pb-32">
      <TopBar />
      <div className="fixed top-14 left-0 right-0 z-30 bg-[#0B0F17]/85 backdrop-blur-md px-4 py-2 border-b border-white/5">
        <button onClick={() => navigate(-1)} data-testid="post-back" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm">
          <ArrowLeft size={16} /> {t("post.cancel")}
        </button>
      </div>

      <main className="max-w-2xl mx-auto pt-24 px-4">
        <div className="mb-6">
          <div className="overline text-amber-400 mb-1">{t("post.eyebrow")}</div>
          <h1 className="font-display font-black text-3xl tracking-tight">{t("post.title")}</h1>
          <p className="text-slate-400 text-sm mt-1">{t("post.sub")}</p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="post-form">
          <Field label={t("post.title_label")}>
            <Input data-testid="post-title" required minLength={3} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("post.title_placeholder")} className="bg-[#131A26] border-white/10 h-11" />
          </Field>

          <Field label={t("post.category")}>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="post-category" className="bg-[#131A26] border-white/10 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((c) => (
                  <SelectItem key={c.key} value={c.key}>{t(`categories.${c.key}`, c.label)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("post.city")}>
              <CityAutocomplete
                testid="post-city"
                required
                value={form.city}
                placeholder={t("post.city_placeholder")}
                onChange={(v) => setForm((p) => ({ ...p, city: v, country: null, country_code: null }))}
                onSelect={(r) => {
                  setForm((p) => ({ ...p, city: r.city, country: r.country, country_code: r.country_code, lat: r.lat, lng: r.lng, timezone: r.timezone || p.timezone }));
                  if (r.timezone && r.timezone !== form.timezone) toast.success(t("post.tz_auto_set", { tz: r.timezone.replace(/_/g, " ") }));
                }}
              />
            </Field>
            <Field label={t("post.venue")}>
              <Input data-testid="post-venue" value={form.venue} onChange={(e) => set("venue", e.target.value)} placeholder={t("post.venue_placeholder")} className="bg-[#131A26] border-white/10 h-11" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("post.starts_at")}>
              <Input data-testid="post-starts-at" type="datetime-local" required value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} className="bg-[#131A26] border-white/10 h-11 text-white" />
            </Field>
            <Field label={t("post.timezone")}>
              <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
                <SelectTrigger data-testid="post-timezone" className="bg-[#131A26] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(ALL_TZ.includes(form.timezone) ? ALL_TZ : [form.timezone, ...ALL_TZ]).map((tz) => (
                    <SelectItem key={tz} value={tz} data-testid={`post-tz-option-${tz}`}>{tz.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label={t("post.location_pin")}>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={useCurrentLocation}
                disabled={geo.loading}
                data-testid="post-use-gps"
                className="flex-1 h-11 rounded-xl border border-white/10 bg-[#131A26] hover:border-emerald-500/40 text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {geo.loading ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}
                {t("post.use_gps")}
              </button>
              <button
                type="button"
                onClick={lookupCity}
                disabled={geocoding}
                data-testid="post-lookup-city"
                className="flex-1 h-11 rounded-xl border border-white/10 bg-[#131A26] hover:border-amber-500/40 text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {geocoding ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
                {t("post.pin_city")}
              </button>
            </div>
            {form.lat != null && form.lng != null && (
              <div className="mt-2 flex items-center gap-2 text-xs text-emerald-400 font-mono" data-testid="post-pinned">
                <Check size={14} />
                {t("post.pinned_at")} {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
                <button type="button" onClick={clearPin} className="text-slate-500 hover:text-rose-400 ml-auto">{t("post.clear")}</button>
              </div>
            )}
          </Field>

          <Field label={t("post.image_label")}>
            <Input data-testid="post-image" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://..." className="bg-[#131A26] border-white/10 h-11" />
          </Field>

          <Field label={t("post.video_label")}>
            <Input data-testid="post-video" value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://youtube.com/shorts/... or https://tiktok.com/@..." className="bg-[#131A26] border-amber-500/20 h-11" />
          </Field>

          <Field label={t("post.description")}>
            <div className="flex justify-end mb-1">
              <button
                type="button"
                onClick={aiGenerate}
                disabled={aiLoading}
                data-testid="post-ai-generate"
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-gradient-to-r from-rose-500/20 to-amber-500/20 border border-amber-500/30 text-xs font-semibold text-amber-200 hover:text-white hover:border-amber-400 transition-colors disabled:opacity-50"
              >
                {aiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {aiLoading ? t("post.writing") : t("post.write_ai")}
              </button>
            </div>
            <Textarea data-testid="post-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} placeholder={t("post.desc_ai_placeholder")} className="bg-[#131A26] border-white/10" />
          </Field>

          <Field label={t("post.ticket_type")}>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "fixed", label: t("post.fixed") },
                { k: "auction", label: t("post.auction") },
                { k: "free", label: t("post.free_rsvp") },
              ].map((tt) => (
                <button
                  key={tt.k}
                  type="button"
                  data-testid={`post-type-${tt.k}`}
                  onClick={() => set("ticket_type", tt.k)}
                  className={`h-11 rounded-xl text-sm font-semibold border transition-colors ${
                    form.ticket_type === tt.k
                      ? "bg-rose-500 border-rose-500 text-white"
                      : "bg-[#131A26] border-white/10 text-slate-300 hover:border-white/25"
                  }`}
                >
                  {tt.label}
                </button>
              ))}
            </div>
          </Field>

          {form.ticket_type !== "free" && (
            <p className="text-xs text-slate-500 -mt-2" data-testid="post-prices-usd-note">{t("post.prices_usd")}</p>
          )}

          {form.ticket_type === "fixed" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={`${t("post.price")} (USD)`}>
                <Input data-testid="post-price" required type="number" min="1" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-[#131A26] border-white/10 h-11" />
              </Field>
              <Field label={t("post.quantity")}>
                <Input data-testid="post-quantity" type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="bg-[#131A26] border-white/10 h-11" />
              </Field>
            </div>
          )}

          {form.ticket_type === "auction" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label={`${t("post.starting_bid")} (USD)`}>
                  <Input data-testid="post-start-bid" required type="number" min="1" step="0.01" value={form.starting_bid} onChange={(e) => set("starting_bid", e.target.value)} className="bg-[#131A26] border-white/10 h-11" />
                </Field>
                <Field label={`${t("post.increment")} (USD)`}>
                  <Input data-testid="post-increment" type="number" min="1" step="0.01" value={form.bid_increment} onChange={(e) => set("bid_increment", e.target.value)} className="bg-[#131A26] border-white/10 h-11" />
                </Field>
              </div>
              <Field label={t("post.auction_ends_at")}>
                <Input data-testid="post-auction-end" type="datetime-local" value={form.auction_ends_at} onChange={(e) => set("auction_ends_at", e.target.value)} className="bg-[#131A26] border-white/10 h-11 text-white" />
              </Field>
            </>
          )}

          <Button type="submit" disabled={busy} data-testid="post-submit" className="w-full h-12 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-xl">
            {busy ? t("post.posting") : t("post.post_event")}
          </Button>
        </form>
      </main>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="overline text-slate-400">{label}</Label>
    {children}
  </div>
);
